/**
 * Feature flags, read from the session payload (ruling R-0912-1 rule 2).
 *
 * THE BROWSER NEVER ASKS THE FLAG VENDOR. The api evaluates every flag locally
 * and hands the whole map over on `GET /me`, which is why this file has no
 * fetch, no SDK and no loading state of its own: a flag arrives with the
 * session or it does not arrive, and `useFlag`'s default decides what the
 * second case means. The alternatives were all worse — a second origin the
 * bundle budget (FR-1.2) has to carry, a flash of the wrong variant while a
 * client SDK bootstraps, and a value an ad blocker gets to choose.
 *
 * `useFlag` TAKES ITS DEFAULT AT THE CALL SITE, and that is the whole design.
 * A kill switch defaults ON (nothing changes until somebody turns it off); a
 * staged rollout defaults OFF (nothing changes until somebody turns it on).
 * Which of the two a flag is, is a fact about the surface, not about the
 * plumbing — so the plumbing refuses to guess.
 *
 * Signed out, or before the session lands, every flag is absent and every
 * caller gets its default.
 */
import { useQuery } from "@tanstack/react-query";
import { meQuery } from "./queries";

export type Flags = Record<string, boolean | string>;

/** Every flag this session carries. Empty when nobody is signed in. */
export function useFlags(): Flags {
  const me = useQuery(meQuery());
  return me.data?.flags ?? {};
}

/**
 * One flag, with the default that applies when it is absent — which is the
 * ordinary case, not an error: a flag nobody has created yet is absent, and so
 * is every flag before the session query resolves.
 *
 * A string value is a multivariate variant, and any non-empty variant counts as
 * on: a surface that cares which variant reads `useFlags()` directly.
 */
export function useFlag(name: string, fallback = false): boolean {
  const value = useFlags()[name];
  if (value === undefined) return fallback;
  return value === true || (typeof value === "string" && value.length > 0);
}
